import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Box, Tabs, Tab, Button, Card, CardContent, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Typography, IconButton, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText, Chip, Stack,
  TextField, InputAdornment, TablePagination
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { 
  Image as ImageIcon, 
  BarChart3, 
  FileText, 
  MessageSquare, 
  Building2,
  Plus,
  Edit,
  Trash2,
  Save,
  Eye,
  Share2,
  Search
} from 'lucide-react';
import { contentAPI, adminAPI } from '../services/api';
import { PageHeader, EmptyState, StatusChip, DataTableCard } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ImageUpload from '../components/ImageUpload';
import MultiImageUpload from '../components/MultiImageUpload';
import AdminRichTextEditor from '../components/AdminRichTextEditor.jsx';
import { normalizeQuillHtml, normalizeQuillEmpty } from '../utils/richHtml.js';
import { buildBlogBentoGridHtml } from '../constants/blogBentoGridHtml.js';
import BlogPreviewModal from '../components/BlogPreviewModal.jsx';
import AdminDatePicker from '../components/AdminDatePicker.jsx';
import './Content.css';
import './ModuleAdmin.css';

const Content = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'hero-sliders';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [blogPreview, setBlogPreview] = useState(null);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const queryClient = useQueryClient();
  const { permissions } = useAuth();
  const { showToast } = useToast();
  const canManageContent = permissions.includes('*') || permissions.includes('content_manage');

  const tabs = [
    { id: 'hero-sliders', label: 'Hero Sliders', icon: ImageIcon },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
    { id: 'partners', label: 'Partners', icon: Building2 },
    { id: 'login-page-images', label: 'Login Page Images', icon: ImageIcon },
  ];

  // Fetch data based on active tab
  const { data, isLoading, error } = useQuery(
    ['content', activeTab],
    async () => {
      try {
        const response = await (() => {
          switch (activeTab) {
            case 'hero-sliders':
              return contentAPI.getHeroSliders();
            case 'stats':
              return contentAPI.getStats();
            case 'blogs':
              return contentAPI.getBlogs();
            case 'testimonials':
              return contentAPI.getTestimonials();
            case 'partners':
              return contentAPI.getPartners();
            case 'login-page-images':
              return contentAPI.getLoginPageImages();
            default:
              return Promise.resolve({ data: { data: [] } });
          }
        })();
        return response.data;
      } catch (err) {
        console.error('Error fetching content:', err);
        return { data: [] };
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(
    (formData) => {
      switch (activeTab) {
        case 'hero-sliders':
          return contentAPI.createHeroSlider(formData);
        case 'stats':
          return formData?.stats ? contentAPI.updateStats(formData) : contentAPI.createStat(formData);
        case 'blogs':
          return contentAPI.createBlog(formData);
        case 'testimonials':
          return contentAPI.createTestimonial(formData);
        case 'partners':
          return contentAPI.createPartner(formData);
        case 'login-page-images':
          return contentAPI.createLoginPageImage(formData);
        default:
          return Promise.resolve();
      }
    },
    {
      onSuccess: (res) => {
        const tab = activeTabRef.current;
        queryClient.invalidateQueries(['content', tab]);
        const row = res?.data?.data;
        if (tab === 'blogs' && row?.id) {
          setBlogPreview({ blog: row });
        }
        setShowForm(false);
        setEditingItem(null);
      },
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, formData }) => {
      switch (activeTab) {
        case 'hero-sliders':
          return contentAPI.updateHeroSlider(id, formData);
        case 'blogs':
          return contentAPI.updateBlog(id, formData);
        case 'testimonials':
          return contentAPI.updateTestimonial(id, formData);
        case 'partners':
          return contentAPI.updatePartner(id, formData);
        case 'login-page-images':
          return contentAPI.updateLoginPageImage(id, formData);
        default:
          return Promise.resolve();
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['content', activeTab]);
        setEditingItem(null);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => {
      switch (activeTab) {
        case 'hero-sliders':
          return contentAPI.deleteHeroSlider(id);
        case 'blogs':
          return contentAPI.deleteBlog(id);
        case 'testimonials':
          return contentAPI.deleteTestimonial(id);
        case 'partners':
          return contentAPI.deletePartner(id);
        case 'stats':
          return contentAPI.deleteStat(id);
        case 'login-page-images':
          return contentAPI.deleteLoginPageImage(id);
        default:
          return Promise.resolve();
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['content', activeTab]);
      },
    }
  );

  const handleEdit = (item) => {
    console.log('Editing item:', item);
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleDuplicateBlog = async (blog) => {
    try {
      const copyData = {
        title: `${blog.title} (Copy)`,
        slug: `${blog.slug}-copy-${Date.now().toString().slice(-4)}`,
        category: blog.category,
        imageUrl: blog.imageUrl,
        shortDescription: blog.shortDescription,
        content: blog.content,
        author: blog.author,
        isPublished: false,
        metaTitle: blog.metaTitle ? `${blog.metaTitle} (Copy)` : null,
        metaDescription: blog.metaDescription
      };
      await createMutation.mutateAsync(copyData);
      showToast('Blog duplicated as draft successfully!', 'success');
    } catch (err) {
      console.error('Error duplicating blog:', err);
      showToast(err?.response?.data?.message || 'Failed to duplicate blog', 'error');
    }
  };

  // Synchronize URL search params with active tab and edit item
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTabRef.current) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    if (activeTab === 'blogs') {
      if (editId === 'new') {
        navigate('/blogs/new');
      } else {
        navigate(`/blogs/edit/${editId}`);
      }
      return;
    }
    if (editId === 'new') {
      setEditingItem(null);
      setShowForm(true);
      return;
    }
    const list = Array.isArray(data?.data) ? data.data : [];
    if (list.length > 0) {
      const match = list.find((item) => String(item.id) === String(editId));
      if (match) {
        setEditingItem(match);
        setShowForm(true);
      }
    }
  }, [searchParams, data?.data, activeTab, navigate]);

  const handleSubmit = (formData, itemId = null) => {
    // Clean formData before submitting
    const cleanedData = cleanFormData(formData, activeTab);
    
    // Use itemId from form if provided, otherwise fall back to editingItem
    const idToUpdate = itemId || editingItem?.id;
    
    console.log('Form submitted:', { editingItem, itemId, idToUpdate, cleanedData });
    
    if (idToUpdate) {
      console.log('Updating item with ID:', idToUpdate);
      updateMutation.mutate({ id: idToUpdate, formData: cleanedData });
    } else {
      console.log('Creating new item');
      createMutation.mutate(cleanedData);
    }
  };
  
  // Helper function to clean form data
  const cleanFormData = (data, type) => {
    const cleaned = { ...data };
    
    // Remove helper fields
    delete cleaned.useMultipleImages;
    delete cleaned.imageUrls;
    
    if (type === 'hero-sliders') {
      // Only keep fields that HeroSlider model expects
      const allowedFields = ['title', 'subtitle', 'imageUrl', 'ctaText', 'ctaLink', 'order', 'isActive'];
      const result = {};
      allowedFields.forEach(field => {
        if (cleaned[field] !== undefined) {
          result[field] = cleaned[field];
        }
      });
      return result;
    }
    
    if (type === 'login-page-images') {
      // Only keep fields that LoginPageImage model expects
      const allowedFields = ['imageUrl', 'position', 'isSpecial', 'specialTitle', 'specialSubtitle', 'isActive', 'order'];
      const result = {};
      allowedFields.forEach(field => {
        if (cleaned[field] !== undefined) {
          result[field] = cleaned[field];
        }
      });
      // Convert position to number
      if (result.position) result.position = Number(result.position);
      // If special, imageUrl should be null
      if (result.isSpecial) result.imageUrl = null;
      return result;
    }

    if (type === 'testimonials') {
      const allowedFields = ['name', 'role', 'imageUrl', 'quote', 'rating', 'eventName', 'galleryImages', 'isActive', 'order'];
      const result = {};
      allowedFields.forEach(field => {
        if (cleaned[field] !== undefined) {
          result[field] = cleaned[field];
        }
      });
      if (result.rating !== undefined) result.rating = Math.min(5, Math.max(1, Number(result.rating) || 5));
      if (result.galleryImages !== undefined && !Array.isArray(result.galleryImages)) {
        result.galleryImages = Array.isArray(cleaned.images) ? cleaned.images : [];
      }
      if (result.order !== undefined) result.order = Number(result.order) || 0;
      return result;
    }

    if (type === 'partners') {
      const allowedFields = ['name', 'logoUrl', 'websiteUrl', 'isActive', 'order'];
      const result = {};
      allowedFields.forEach(field => {
        if (cleaned[field] !== undefined) {
          result[field] = cleaned[field];
        }
      });
      if (result.name !== undefined) result.name = String(result.name || '').trim();
      if (result.logoUrl !== undefined) result.logoUrl = String(result.logoUrl || '').trim();
      if (result.websiteUrl !== undefined) result.websiteUrl = String(result.websiteUrl || '').trim() || null;
      if (result.isActive !== undefined) result.isActive = Boolean(result.isActive);
      if (result.order !== undefined) result.order = Number(result.order) || 0;
      return result;
    }

    return cleaned;
  };

  const items = Array.isArray(data?.data) ? data.data : (data?.data ? [data.data] : []);

  const handlePreviewSaveLayout = useCallback(
    async (html, blogId) => {
      if (!blogId) return;
      try {
        const normalized = normalizeQuillHtml(html);
        await contentAPI.updateBlog(blogId, { content: normalized });
        showToast({ message: 'Article layout saved to this post.', severity: 'success' });
        queryClient.invalidateQueries(['content', 'blogs']);
        setBlogPreview((prev) =>
          prev?.blog?.id === blogId ? { ...prev, blog: { ...prev.blog, content: normalized } } : prev
        );
      } catch (err) {
        showToast({
          message: err?.response?.data?.message || 'Could not save layout.',
          severity: 'error'
        });
      }
    },
    [queryClient, showToast]
  );

  return (
    <div className="module-page content-page">
      <PageHeader
        title="Content Management"
        subtitle="Manage all dynamic content for your website"
        actions={
          canManageContent ? (
            activeTab !== 'stats' ? (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                type="button"
                onClick={() => {
                  if (activeTab === 'blogs') {
                    navigate('/blogs/new');
                    return;
                  }
                  setEditingItem(null);
                  setShowForm(true);
                }}
              >
                Add New
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingItem(null);
                    setShowForm(true);
                  }}
                  startIcon={<AddRoundedIcon />}
                  sx={{ mr: 1 }}
                >
                  Add Stat
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setEditingItem(data?.data || []);
                    setShowForm(true);
                  }}
                  startIcon={<EditRoundedIcon />}
                >
                  Edit Stats
                </Button>
              </>
            )
          ) : null
        }
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, tabId) => {
            setActiveTab(tabId);
            setShowForm(false);
            setEditingItem(null);
            const next = new URLSearchParams(searchParams);
            next.set('tab', tabId);
            next.delete('edit');
            setSearchParams(next, { replace: true });
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Icon size={16} />
                    <span>{tab.label}</span>
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Card>

      <Box className="content-area">
        {error && (
          <EmptyState
            title="Error loading content"
            description="Please try again."
            sx={{ borderColor: 'error.light', mb: 2 }}
          />
        )}
        {isLoading ? (
          <Card>
            <CardContent>
              <div className="ops-loading-box">
                <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">Loading content…</Typography>
              </div>
            </CardContent>
          </Card>
        ) : showForm ? (
          <ContentForm
            key={editingItem?.id || (Array.isArray(editingItem) ? 'stats-bulk' : 'new')}
            type={activeTab}
            item={editingItem}
            isEditing={!!(editingItem?.id || (Array.isArray(editingItem) && editingItem.length))}
            onCancel={() => {
              setShowForm(false);
              setEditingItem(null);
              if (searchParams.get('edit')) {
                const next = new URLSearchParams(searchParams);
                next.delete('edit');
                setSearchParams(next, { replace: true });
              }
            }}
            onSubmit={handleSubmit}
            isLoading={createMutation.isLoading || updateMutation.isLoading}
            onPreview={
              activeTab === 'blogs'
                ? (snapshot) =>
                    setBlogPreview({
                      blog: snapshot,
                      isUnsavedPreview: !snapshot?.id
                    })
                : undefined
            }
          />
        ) : (
          <ContentList
            type={activeTab}
            items={items}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicateBlog}
            showToast={showToast}
          />
        )}
      </Box>

      <BlogPreviewModal
        open={Boolean(blogPreview?.blog)}
        blog={blogPreview?.blog}
        isUnsavedPreview={Boolean(blogPreview?.isUnsavedPreview)}
        onClose={() => setBlogPreview(null)}
        onEditInAdmin={(b) => {
          setBlogPreview(null);
          setEditingItem(b);
          setShowForm(true);
        }}
        onSaveLayout={handlePreviewSaveLayout}
        publicSiteBaseUrl={import.meta.env.VITE_PUBLIC_SITE_URL || ''}
      />
    </div>
  );
};

function formatBlogDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '-';
  }
}

// Dedicated Blog List Table with Pills, Search, Pagination, balanced columns, Views, and Actions
const BlogListTable = ({ blogs, onEdit, onDelete, onDuplicate, showToast }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuBlog, setMenuBlog] = useState(null);

  const handleOpenMenu = (e, blog) => {
    setMenuAnchor(e.currentTarget);
    setMenuBlog(blog);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuBlog(null);
  };

  const blogsArray = Array.isArray(blogs) ? blogs : [];
  const allCount = blogsArray.length;
  const publishedCount = blogsArray.filter((b) => b.isPublished).length;
  const draftCount = blogsArray.filter((b) => !b.isPublished).length;

  const filteredBlogs = blogsArray.filter((b) => {
    if (statusFilter === 'published' && !b.isPublished) return false;
    if (statusFilter === 'draft' && b.isPublished) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const matchTitle = (b.title || '').toLowerCase().includes(q);
      const matchCategory = (b.category || '').toLowerCase().includes(q);
      const matchSlug = (b.slug || '').toLowerCase().includes(q);
      const matchAuthor = (b.author || '').toLowerCase().includes(q);
      if (!matchTitle && !matchCategory && !matchSlug && !matchAuthor) {
        return false;
      }
    }
    return true;
  });

  const paginatedBlogs = filteredBlogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Top Bar: Pills on left, Search on right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`All (${allCount})`}
            clickable
            color={statusFilter === 'all' ? 'primary' : 'default'}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            onClick={() => {
              setStatusFilter('all');
              setPage(0);
            }}
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '9999px',
              px: 0.5,
              cursor: 'pointer'
            }}
          />
          <Chip
            label={`Published (${publishedCount})`}
            clickable
            color={statusFilter === 'published' ? 'success' : 'default'}
            variant={statusFilter === 'published' ? 'filled' : 'outlined'}
            onClick={() => {
              setStatusFilter('published');
              setPage(0);
            }}
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '9999px',
              px: 0.5,
              cursor: 'pointer'
            }}
          />
          <Chip
            label={`Drafts (${draftCount})`}
            clickable
            color={statusFilter === 'draft' ? 'warning' : 'default'}
            variant={statusFilter === 'draft' ? 'filled' : 'outlined'}
            onClick={() => {
              setStatusFilter('draft');
              setPage(0);
            }}
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '9999px',
              px: 0.5,
              cursor: 'pointer'
            }}
          />
        </Box>

        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search blogs..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} style={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setPage(0);
                  }}
                  edge="end"
                  sx={{ p: 0.5 }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            width: { xs: '100%', sm: 260 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '9999px',
              bgcolor: '#fff',
              fontSize: '0.8125rem',
              height: 36,
              pr: 1
            }
          }}
        />
      </Box>

      {/* Table Card */}
      <DataTableCard>
        {filteredBlogs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? `No blogs matching "${searchQuery}" in ${statusFilter}.`
                : `No blogs found in ${statusFilter}.`}
            </Typography>
          </Box>
        ) : (
          <>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: { xs: '40%', sm: '32%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Blog Name
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: '20%', sm: '16%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: '20%', sm: '15%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Publish Date
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: '15%', sm: '13%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: '15%', sm: '10%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Views
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: { xs: '15%', sm: '14%' },
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#475569',
                      bgcolor: '#f8fafc',
                      py: 1.25
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBlogs.map((item) => {
                  const dateDisplay = item.isPublished
                    ? formatBlogDate(item.publishedAt || item.createdAt)
                    : '-';

                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f8fafc' },
                        transition: 'background-color 0.15s ease'
                      }}
                      onClick={(e) => {
                        if (
                          e.target.closest('button') ||
                          e.target.closest('.MuiIconButton-root') ||
                          e.target.closest('.MuiMenu-root') ||
                          e.target.closest('a')
                        ) {
                          return;
                        }
                        window.open(`/blogs/edit/${item.id}`, '_blank');
                      }}
                    >
                      {/* Blog Name Column */}
                      <TableCell sx={{ py: 1.25, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <img
                            src={item.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=120&q=80'}
                            alt=""
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              objectFit: 'cover',
                              backgroundColor: '#f1f5f9',
                              flexShrink: 0,
                              border: '1px solid #e2e8f0'
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#0f172a',
                                lineHeight: 1.35,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': { color: 'primary.main' }
                              }}
                              title={item.title}
                            >
                              {item.title || 'Untitled Blog'}
                            </Typography>
                            {item.slug && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#64748b',
                                  display: 'block',
                                  mt: 0.25,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                /blogs/{item.slug}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Category Column */}
                      <TableCell sx={{ py: 1.25, overflow: 'hidden' }}>
                        {item.category ? (
                          <Chip
                            label={item.category}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              borderColor: '#e2e8f0',
                              bgcolor: '#f8fafc',
                              color: '#334155',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem' }}>
                            Uncategorized
                          </Typography>
                        )}
                      </TableCell>

                      {/* Publish Date Column */}
                      <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontSize: '0.8125rem' }}>
                          {dateDisplay}
                        </Typography>
                      </TableCell>

                      {/* Status Column */}
                      <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                        <StatusChip
                          status={item.isPublished ? 'active' : 'draft'}
                          label={item.isPublished ? 'Published' : 'Draft'}
                          size="small"
                        />
                      </TableCell>

                      {/* Views (count) Column */}
                      <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, color: '#475569' }}>
                          <Eye size={15} style={{ opacity: 0.65 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.8125rem' }}>
                            {Number(item.views || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Action Column */}
                      <TableCell align="right" sx={{ py: 1.25 }} onClick={(e) => e.stopPropagation()}>
                        <Box className="content-inline-actions" sx={{ justifyContent: 'flex-end', display: 'flex', gap: 0.5 }}>
                          {/* Edit Button */}
                          <Tooltip title="Edit blog (new tab)">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/blogs/edit/${item.id}`, '_blank');
                              }}
                              aria-label="Edit blog"
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Share Button */}
                          <Tooltip title="Copy public link">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const publicBase = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://invinciblengo.org';
                                const url = `${publicBase}/blogs/${item.slug}`;
                                if (navigator?.clipboard?.writeText) {
                                  navigator.clipboard.writeText(url);
                                  showToast?.('Public blog link copied to clipboard!', 'success');
                                }
                              }}
                              aria-label="Share blog"
                            >
                              <Share2 size={16} />
                            </IconButton>
                          </Tooltip>

                          {/* 3 Dots Menu Button */}
                          <Tooltip title="More options">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMenu(e, item);
                              }}
                              aria-label="More options"
                            >
                              <MoreVertRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredBlogs.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid #e2e8f0',
                '.MuiTablePagination-toolbar': {
                  minHeight: 48,
                  px: 2
                }
              }}
            />
          </>
        )}
      </DataTableCard>

      {/* 3 Dots Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { minWidth: 175, borderRadius: 2, py: 0.5, boxShadow: '0 8px 24px rgba(15,23,42,0.12)' }
          }
        }}
      >
        <MenuItem
          onClick={() => {
            const publicBase = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://invinciblengo.org';
            window.open(`${publicBase}/blogs/${menuBlog?.slug}`, '_blank');
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <OpenInNewRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Blog" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <MenuItem
          onClick={() => {
            const target = menuBlog;
            handleCloseMenu();
            onDuplicate?.(target);
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicate Blog" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        <MenuItem
          onClick={() => {
            const id = menuBlog?.id;
            handleCloseMenu();
            onDelete?.(id);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteRoundedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete Blog" primaryTypographyProps={{ variant: 'body2', color: 'error' }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Content List Component
const ContentList = ({ type, items, onEdit, onDelete, onDuplicate, showToast }) => {
  // Ensure items is always an array
  const itemsArray = Array.isArray(items) ? items : [];

  if (type === 'blogs') {
    return (
      <BlogListTable
        blogs={itemsArray}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        showToast={showToast}
      />
    );
  }

  if (itemsArray.length === 0) {
    return (
      <Card>
        <CardContent>
          <EmptyState title="No items found" description="Click Add New to create one." />
        </CardContent>
      </Card>
    );
  }

  if (type === 'hero-sliders') {
    return (
      <div className="content-grid">
        {itemsArray.map((item) => (
          <div key={item.id} className="content-card">
            <div className="card-image">
              <img src={item.imageUrl} alt={item.title} />
            </div>
            <div className="card-content">
              <h3 className="card-title">{item.title}</h3>
              <p className="card-subtitle">{item.subtitle}</p>
              <div className="card-actions">
                <button 
                  className="action-btn-primary" 
                  onClick={() => onEdit(item)}
                  title="Edit"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button 
                  className="action-btn-danger" 
                  onClick={() => onDelete(item.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'stats') {
    return (
      <div className="stats-grid">
        {itemsArray.map((stat) => (
          <div key={stat.id} className="stat-card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
            <div className="card-actions" style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                className="action-btn-danger"
                onClick={() => onDelete(stat.id)}
                title="Delete"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'login-page-images') {
    return (
      <div className="content-grid">
        {itemsArray.map((item) => (
          <div key={item.id} className="content-card">
            {item.isSpecial ? (
              <div className="card-image" style={{ background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '700', textAlign: 'center' }}>
                  {item.specialTitle || 'HIKER SUMMIT'}
                  <br />
                  {item.specialSubtitle || 'EXPLORE NOW'}
                </div>
              </div>
            ) : (
              <div className="card-image">
                <img src={item.imageUrl || 'https://via.placeholder.com/300'} alt={`Position ${item.position}`} />
              </div>
            )}
            <div className="card-content">
              <h3 className="card-title">Position {item.position}</h3>
              <p className="card-subtitle">{item.isSpecial ? 'Special Card' : 'Image'}</p>
              <div className="card-actions">
                <button 
                  className="action-btn-primary" 
                  onClick={() => onEdit(item)}
                  title="Edit"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button 
                  className="action-btn-danger" 
                  onClick={() => onDelete(item.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'testimonials') {
    return (
      <div className="content-grid">
        {itemsArray.map((item) => (
          <div key={item.id} className="content-card">
            <div className="card-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <img
                src={item.imageUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'}
                alt={item.name}
                style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }}
              />
            </div>
            <div className="card-content">
              <h3 className="card-title">{item.name}</h3>
              <p className="card-subtitle" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {'★'.repeat(item.rating || 5)}{'☆'.repeat(5 - (item.rating || 5))} {item.rating || 5}/5
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--cui-body-color-secondary)', marginTop: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {item.quote || item.review || '-'}
              </p>
              {(item.galleryImages?.length || item.images?.length) > 0 && (
                <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {(item.galleryImages || item.images || []).length} image(s)
                </p>
              )}
              <div className="card-actions">
                <button 
                  className="action-btn-primary" 
                  onClick={() => onEdit(item)}
                  title="Edit"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button 
                  className="action-btn-danger" 
                  onClick={() => onDelete(item.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'partners') {
    return (
      <div className="content-grid">
        {itemsArray.map((item) => (
          <div key={item.id} className="content-card">
            <div className="card-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', padding: '1rem' }}>
              {item.logoUrl ? (
                <img src={item.logoUrl} alt={item.name} style={{ maxWidth: '180px', maxHeight: '56px', objectFit: 'contain' }} />
              ) : (
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No logo</div>
              )}
            </div>
            <div className="card-content">
              <h3 className="card-title">{item.name}</h3>
              <p className="card-subtitle">{item.websiteUrl || 'No website URL'}</p>
              <div className="card-actions">
                <button
                  className="action-btn-primary"
                  onClick={() => onEdit(item)}
                  title="Edit"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                <button
                  className="action-btn-danger"
                  onClick={() => onDelete(item.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default table view for other content types
  return (
    <DataTableCard>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>ID</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Title/Name</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Status</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsArray.map((item) => {
            const statusFlag = type === 'blogs' ? item.isPublished : item.isActive;
            const statusText = type === 'blogs'
              ? (statusFlag ? 'Published' : 'Draft')
              : (statusFlag ? 'Active' : 'Inactive');
            const titleText = item.title || item.name || item.slug || '-';
            return (
            <TableRow key={item.id} hover>
              <TableCell className="content-mono-xs">{item.id}</TableCell>
              <TableCell className="content-font-medium">{titleText}</TableCell>
              <TableCell>
                <StatusChip
                  status={statusFlag ? (type === 'blogs' ? 'active' : 'active') : 'draft'}
                  label={statusText}
                />
              </TableCell>
              <TableCell align="right">
                <Box className="content-inline-actions" sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(item)} aria-label="Edit">
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(item.id)} aria-label="Delete">
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          )})}
        </TableBody>
      </Table>
    </DataTableCard>
  );
};

/**
 * HTML datetime-local values must be local wall time (YYYY-MM-DDTHH:mm), no timezone.
 * Using toISOString().slice(0, 16) is UTC and breaks controlled sync in Safari (empty field vs picker).
 */
function formatForDatetimeLocal(value) {
  if (value === null || value === undefined || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayYmdLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function splitLocalDateTime(value) {
  const s = formatForDatetimeLocal(value);
  if (!s) return { date: '', time: '' };
  const [date, time = ''] = s.split('T');
  return { date: date || '', time: time.slice(0, 5) };
}

function mergeLocalDateTime(date, time) {
  if (!date) return '';
  const t = time && time.length >= 4 ? time.slice(0, 5) : '12:00';
  return `${date}T${t}`;
}

// Content Form Component
const ContentForm = ({ type, item, isEditing = false, onCancel, onSubmit, isLoading, onPreview }) => {
  const slugify = (value = '') =>
    String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  const getInitialData = () => {
    if (type === 'stats' && item && Array.isArray(item)) {
      return { stats: item };
    }
    if (type === 'stats' && !item) {
      return { label: '', value: '' };
    }
    
    const defaultData = item || getDefaultFormData(type);
    
    // For hero sliders, check if we should initialize multiple images
    if (type === 'hero-sliders' && item) {
      // If item has imageUrls array or multiple image-related fields, enable multiple
      const hasMultipleImages = item.imageUrls && Array.isArray(item.imageUrls) && item.imageUrls.length > 0;
      return {
        ...defaultData,
        ...item,
        useMultipleImages: hasMultipleImages || false,
        imageUrls: item.imageUrls || (item.imageUrl ? [item.imageUrl] : [])
      };
    }
    
    return defaultData;
  };
  
  const [formData, setFormData] = useState(() => getInitialData());
  const [bentoInsertChoice, setBentoInsertChoice] = useState('6');
  const [blogBentoFiles, setBlogBentoFiles] = useState([]);
  const [blogBentoBusy, setBlogBentoBusy] = useState(false);
  const blogContentEditorRef = useRef(null);
  const { showToast } = useToast();
  const { permissions } = useAuth();
  const canEditSlugs = permissions.includes('*') || permissions.includes('slug_manage');

  // Update form data when item prop changes (for editing)
  useEffect(() => {
    if (type === 'stats' && item && Array.isArray(item)) {
      setFormData({ stats: item });
    } else if (type === 'stats' && !item) {
      setFormData({ label: '', value: '' });
    } else if (type === 'hero-sliders' && item) {
      const hasMultipleImages = item.imageUrls && Array.isArray(item.imageUrls) && item.imageUrls.length > 0;
      setFormData({
        ...item,
        useMultipleImages: hasMultipleImages || false,
        imageUrls: item.imageUrls || (item.imageUrl ? [item.imageUrl] : [])
      });
    } else if (item) {
      setFormData(item);
    } else {
      setFormData(getDefaultFormData(type));
    }
  }, [item, type]);

  useEffect(() => {
    setBlogBentoFiles([]);
  }, [bentoInsertChoice]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Prepare data for submission - only include fields that backend expects
    const submitData = { ...formData };
    if (type === 'blogs' && blogContentEditorRef.current?.getHtml) {
      const live = blogContentEditorRef.current.getHtml();
      if (normalizeQuillEmpty(live).length > 0) {
        submitData.content = live;
      }
    }
    
    // If using multiple images, ensure imageUrl is set to first image
    if (submitData.useMultipleImages && submitData.imageUrls && submitData.imageUrls.length > 0) {
      submitData.imageUrl = submitData.imageUrls[0];
    }
    
    // Client-side validation
    if (type === 'hero-sliders') {
      if (!submitData.title || submitData.title.trim() === '') {
        showToast({ message: 'Title is required', severity: 'warning' });
        return;
      }
      if (!submitData.imageUrl || submitData.imageUrl.trim() === '') {
        showToast({
          message: 'Image is required. Please upload an image or enter an image URL.',
          severity: 'warning'
        });
        return;
      }
    }
    
    if (type === 'login-page-images') {
      if (!submitData.position || submitData.position < 1 || submitData.position > 9) {
        showToast({ message: 'Position must be between 1 and 9', severity: 'warning' });
        return;
      }
      if (!submitData.isSpecial && (!submitData.imageUrl || submitData.imageUrl.trim() === '')) {
        showToast({ message: 'Image is required for non-special items', severity: 'warning' });
        return;
      }
      if (submitData.isSpecial && (!submitData.specialTitle || !submitData.specialSubtitle)) {
        showToast({
          message: 'Special title and subtitle are required for special items',
          severity: 'warning'
        });
        return;
      }
    }

    if (type === 'blogs') {
      if (!submitData.title || submitData.title.trim() === '') {
        showToast({ message: 'Blog title is required', severity: 'warning' });
        return;
      }
      if (!submitData.imageUrl || submitData.imageUrl.trim() === '') {
        showToast({ message: 'Blog image is required', severity: 'warning' });
        return;
      }
      const resolvedSlug = submitData.slug?.trim() ? slugify(submitData.slug) : slugify(submitData.title);
      if (!resolvedSlug) {
        showToast({ message: 'Please enter a valid slug', severity: 'warning' });
        return;
      }
    }

    if (type === 'testimonials') {
      if (!submitData.name || submitData.name.trim() === '') {
        showToast({ message: 'Name is required', severity: 'warning' });
        return;
      }
      if (!submitData.quote || submitData.quote.trim() === '') {
        showToast({ message: 'Review is required', severity: 'warning' });
        return;
      }
    }

    // Clean up data - only keep fields that exist in the model
    const cleanedData = {};
    
    if (type === 'hero-sliders') {
      // Only include fields that HeroSlider model expects
      cleanedData.title = (submitData.title && submitData.title.trim()) || '';
      cleanedData.subtitle = (submitData.subtitle && submitData.subtitle.trim()) || null;
      cleanedData.imageUrl = (submitData.imageUrl && submitData.imageUrl.trim()) || '';
      cleanedData.ctaText = (submitData.ctaText && submitData.ctaText.trim()) || 'Explore Camps';
      cleanedData.ctaLink = (submitData.ctaLink && submitData.ctaLink.trim()) || null;
      cleanedData.order = submitData.order !== undefined ? Number(submitData.order) : 0;
      cleanedData.isActive = submitData.isActive !== undefined ? Boolean(submitData.isActive) : true;
      
      // Remove null/empty optional fields
      if (cleanedData.subtitle === null || cleanedData.subtitle === '') {
        delete cleanedData.subtitle;
      }
      if (cleanedData.ctaLink === null || cleanedData.ctaLink === '') {
        delete cleanedData.ctaLink;
      }
    } else if (type === 'blogs') {
      cleanedData.title = submitData.title.trim();
      cleanedData.slug = submitData.slug?.trim() ? slugify(submitData.slug) : slugify(submitData.title);
      cleanedData.category = submitData.category?.trim() || null;
      cleanedData.imageUrl = submitData.imageUrl.trim();
      cleanedData.shortDescription = submitData.shortDescription?.trim() || null;
      const contentNorm = normalizeQuillHtml(submitData.content);
      cleanedData.content = contentNorm || null;
      cleanedData.author = submitData.author?.trim() || null;
      cleanedData.isPublished = Boolean(submitData.isPublished);
      cleanedData.metaTitle = submitData.metaTitle?.trim() || null;
      cleanedData.metaDescription = submitData.metaDescription?.trim() || null;
      cleanedData.publishedAt = cleanedData.isPublished
        ? (submitData.publishedAt ? new Date(submitData.publishedAt) : (item?.publishedAt || new Date()))
        : null;
    } else if (type === 'testimonials') {
      cleanedData.name = (submitData.name && submitData.name.trim()) || '';
      cleanedData.role = (submitData.role && submitData.role.trim()) || null;
      cleanedData.imageUrl = (submitData.imageUrl && submitData.imageUrl.trim()) || null;
      cleanedData.quote = (submitData.quote && submitData.quote.trim()) || '';
      cleanedData.rating = Math.min(5, Math.max(1, Number(submitData.rating) || 5));
      cleanedData.eventName = (submitData.eventName && submitData.eventName.trim()) || null;
      cleanedData.galleryImages = Array.isArray(submitData.galleryImages) ? submitData.galleryImages : [];
      cleanedData.isActive = submitData.isActive !== undefined ? Boolean(submitData.isActive) : true;
      cleanedData.order = submitData.order !== undefined ? Number(submitData.order) : 0;
    } else {
      // For other types, remove helper fields
      delete submitData.useMultipleImages;
      delete submitData.imageUrls;
      Object.assign(cleanedData, submitData);
    }
    
    console.log('Submitting cleaned data:', { cleanedData, isEditing, itemId: item?.id });
    // Pass editing info along with the data
    onSubmit(cleanedData, isEditing ? item?.id : null);
  };

  if (type === 'hero-sliders') {
    return (
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="input"
            required
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <textarea
            name="subtitle"
            value={formData.subtitle}
            onChange={handleChange}
            className="input"
            rows="3"
          />
        </div>
        <div className="form-group">
          <div className="content-toggle-row">
            <input
              type="checkbox"
              id="use-multiple-images"
              checked={formData.useMultipleImages || false}
              onChange={(e) => {
                const useMultiple = e.target.checked;
                setFormData(prev => {
                  if (useMultiple) {
                    // Switching to multiple: convert current imageUrl to array
                    const currentImages = prev.imageUrl 
                      ? (Array.isArray(prev.imageUrl) ? prev.imageUrl : [prev.imageUrl])
                      : [];
                    return {
                      ...prev,
                      useMultipleImages: true,
                      imageUrls: currentImages,
                      imageUrl: currentImages[0] || ''
                    };
                  } else {
                    // Switching to single: use first image from array
                    const firstImage = Array.isArray(prev.imageUrls) && prev.imageUrls.length > 0
                      ? prev.imageUrls[0]
                      : prev.imageUrl || '';
                    return {
                      ...prev,
                      useMultipleImages: false,
                      imageUrl: firstImage,
                      imageUrls: []
                    };
                  }
                });
              }}
              className="content-checkbox-input"
            />
            <label htmlFor="use-multiple-images" className="content-checkbox-label-text">
              Use Multiple Images
            </label>
            <span className="content-checkbox-hint">Upload up to 10 images</span>
          </div>
        </div>
        
        {formData.useMultipleImages ? (
          <div className="multi-upload-wrapper">
            <MultiImageUpload
              label="Hero Images"
              value={formData.imageUrls || (formData.imageUrl ? [formData.imageUrl] : [])}
              onChange={(images) => {
                setFormData(prev => ({ 
                  ...prev, 
                  imageUrls: images, 
                  imageUrl: images.length > 0 ? images[0] : '' 
                }));
              }}
              required
              maxImages={10}
              uploadImage={async (file) => {
                const res = await adminAPI.uploadImage(file);
                return res.data?.data?.url || res.data?.url;
              }}
            />
          </div>
        ) : (
          <ImageUpload
            label="Hero Image"
            value={formData.imageUrl || ''}
            onChange={(value) => setFormData(prev => ({ ...prev, imageUrl: value }))}
            required
            uploadImage={async (file) => {
              const res = await adminAPI.uploadImage(file);
              return res.data?.data?.url || res.data?.url;
            }}
          />
        )}
        <div className="form-group">
          <label>CTA Text</label>
          <input
            type="text"
            name="ctaText"
            value={formData.ctaText}
            onChange={handleChange}
            className="input"
          />
        </div>
        <div className="form-group">
          <label>CTA Link</label>
          <input
            type="text"
            name="ctaLink"
            value={formData.ctaLink}
            onChange={handleChange}
            className="input"
            placeholder="/events or https://example.com/events"
          />
          <p className="form-hint">Enter a relative path (e.g., /events) or full URL</p>
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />
            Active
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    );
  }

  if (type === 'stats') {
    const statsData = formData.stats || [];
    const isBulkEdit = Array.isArray(formData.stats) && formData.stats.length > 0;

    if (isBulkEdit) {
      return (
        <form onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ stats: statsData });
        }} className="content-form">
          <div className="stats-form-grid">
            {statsData.map((stat, index) => (
              <div key={stat.id || index} className="form-group">
                <label>{stat.label} *</label>
                <input
                  type="text"
                  value={stat.value || ''}
                  onChange={(e) => {
                    const newStats = [...statsData];
                    newStats[index] = { ...newStats[index], value: e.target.value };
                    setFormData(prev => ({ ...prev, stats: newStats }));
                  }}
                  className="input"
                  required
                />
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              <Save size={18} className="content-icon-right" />
              {isLoading ? 'Saving...' : 'Save Stats'}
            </button>
          </div>
        </form>
      );
    }

    return (
      <form onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ label: formData.label, value: formData.value });
      }} className="content-form">
        <div className="form-group">
          <label>Label *</label>
          <input
            type="text"
            name="label"
            value={formData.label || ''}
            onChange={handleChange}
            className="input"
            required
          />
        </div>
        <div className="form-group">
          <label>Value *</label>
          <input
            type="text"
            name="value"
            value={formData.value || ''}
            onChange={handleChange}
            className="input"
            required
          />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : 'Add Stat'}
          </button>
        </div>
      </form>
    );
  }

  if (type === 'blogs') {
    return (
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label>Blog Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title || ''}
            onChange={(e) => {
              const nextTitle = e.target.value;
              setFormData(prev => {
                const currentSlug = prev.slug || '';
                const shouldAutoGenerateSlug = !isEditing || !currentSlug || currentSlug === slugify(prev.title || '');
                return {
                  ...prev,
                  title: nextTitle,
                  slug: shouldAutoGenerateSlug ? slugify(nextTitle) : currentSlug
                };
              });
            }}
            className="input"
            required
          />
        </div>

        <div className="form-group">
          <label>Slug *</label>
          <input
            type="text"
            name="slug"
            value={formData.slug || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, slug: slugify(e.target.value) }))}
            className="input"
            required
            disabled={!canEditSlugs}
          />
          <p className="form-hint">Used in URL: /blogs/your-slug</p>
        </div>

        <ImageUpload
          label="Blog Cover Image"
          value={formData.imageUrl || ''}
          onChange={(value) => setFormData(prev => ({ ...prev, imageUrl: value }))}
          required
          uploadImage={async (file) => {
            const res = await adminAPI.uploadImage(file);
            return res.data?.data?.url || res.data?.url;
          }}
        />

        <div className="form-group">
          <label>Category</label>
          <input
            type="text"
            name="category"
            value={formData.category || ''}
            onChange={handleChange}
            className="input"
            placeholder="TREKKING TIPS / DESTINATIONS / COMMUNITY"
          />
        </div>

        <div className="form-group">
          <label>Author</label>
          <input
            type="text"
            name="author"
            value={formData.author || ''}
            onChange={handleChange}
            className="input"
            placeholder="Admin"
          />
        </div>

        <div className="form-group">
          <label>Short Description</label>
          <textarea
            name="shortDescription"
            value={formData.shortDescription || ''}
            onChange={handleChange}
            className="input"
            rows="3"
            placeholder="Brief summary shown in blog cards"
          />
        </div>

        <div className="form-group form-group-blog-content">
          <div className="content-blog-content-toolbar">
            <label>Blog Content</label>
            <div className="content-blog-bento-insert content-blog-bento-panel">
              <select
                className="input"
                aria-label="Image grid size"
                value={bentoInsertChoice}
                onChange={(e) => setBentoInsertChoice(e.target.value)}
              >
                <option value="3">3 images</option>
                <option value="4">4 images</option>
                <option value="6">6 images (bento)</option>
              </select>
              <label className="content-blog-bento-file-label btn btn-secondary">
                Choose {bentoInsertChoice} images
                <input
                  key={bentoInsertChoice}
                  type="file"
                  accept="image/*"
                  multiple
                  className="content-blog-bento-file-input"
                  onChange={(e) => {
                    const n = Number(bentoInsertChoice) || 6;
                    const picked = Array.from(e.target.files || []).slice(0, n);
                    setBlogBentoFiles(picked);
                    e.target.value = '';
                  }}
                />
              </label>
              <span className="content-blog-bento-file-count">
                {blogBentoFiles.length}/{bentoInsertChoice} selected
              </span>
              <button
                type="button"
                className="btn btn-primary"
                disabled={
                  blogBentoBusy ||
                  blogBentoFiles.length < Number(bentoInsertChoice || 6)
                }
                onClick={async () => {
                  const n = Number(bentoInsertChoice) || 6;
                  if (blogBentoFiles.length < n) {
                    showToast({
                      message: `Choose exactly ${n} image file(s) for this grid.`,
                      severity: 'warning'
                    });
                    return;
                  }
                  setBlogBentoBusy(true);
                  try {
                    const urls = [];
                    for (const file of blogBentoFiles.slice(0, n)) {
                      const res = await adminAPI.uploadImage(file);
                      const url = res.data?.data?.url || res.data?.url;
                      if (!url) throw new Error('Upload did not return a URL');
                      urls.push(String(url).trim());
                    }
                    const snippet = buildBlogBentoGridHtml(n, urls);
                    const live = blogContentEditorRef.current?.getHtml?.() ?? '';
                    setFormData((prev) => {
                      const base =
                        normalizeQuillEmpty(live).length > 0 ? live : (prev.content || '');
                      const merged = `${base}${snippet}`;
                      queueMicrotask(() =>
                        blogContentEditorRef.current?.applyHtml?.(merged)
                      );
                      return { ...prev, content: merged };
                    });
                    setBlogBentoFiles([]);
                    showToast({ message: 'Image grid added to the article.', severity: 'success' });
                  } catch (err) {
                    showToast({
                      message: err?.message || 'Could not upload or insert grid.',
                      severity: 'error'
                    });
                  } finally {
                    setBlogBentoBusy(false);
                  }
                }}
              >
                {blogBentoBusy ? 'Uploading…' : 'Upload & add grid'}
              </button>
            </div>
          </div>
          <p className="form-hint" style={{ marginTop: 0 }}>
            <strong>Photo grid:</strong> pick 3 / 4 / 6, choose that many files with <strong>Choose … images</strong>,
            then <strong>Upload & add grid</strong> — images upload first, then the grid is appended to the article. Other
            pictures in the body still use the toolbar <strong>Image</strong> button. <strong>Preview</strong> is
            read-only; edit here, then preview again. In preview <strong>Layout mode</strong>, drag blocks or tiles to
            reorder on saved posts.
          </p>
          <AdminRichTextEditor
            key={item?.id ? `blog-content-${item.id}` : 'blog-content-new'}
            ref={blogContentEditorRef}
            value={formData.content || ''}
            onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
            placeholder="Headings, lists, quotes, code, images, and tables — use the toolbar."
            hint="Images: click where you want the image (or select a grid tile), then toolbar Image — upload a file or paste a URL."
            className="blog-content-rich-editor"
            uploadImage={async (file) => {
              const res = await adminAPI.uploadImage(file);
              return res.data?.data?.url || res.data?.url;
            }}
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isPublished"
              checked={Boolean(formData.isPublished)}
              onChange={handleChange}
            />
            Publish now
          </label>
        </div>

        <div className="form-group">
          <label>Published At</label>
          <div className="content-published-at-row">
            <AdminDatePicker
              label="Publication date"
              value={splitLocalDateTime(formData.publishedAt).date}
              onChange={(date) => {
                const { time } = splitLocalDateTime(formData.publishedAt);
                setFormData((prev) => ({
                  ...prev,
                  publishedAt: mergeLocalDateTime(date, time || '12:00')
                }));
              }}
              disabled={!formData.isPublished}
              sx={{ minWidth: 160 }}
            />
            <input
              type="time"
              className="input content-published-at-time"
              value={splitLocalDateTime(formData.publishedAt).time}
              onChange={(e) => {
                const timeVal = e.target.value;
                let { date } = splitLocalDateTime(formData.publishedAt);
                if (!date) date = todayYmdLocal();
                setFormData((prev) => ({
                  ...prev,
                  publishedAt: mergeLocalDateTime(date, timeVal)
                }));
              }}
              disabled={!formData.isPublished}
              aria-label="Publication time"
              step={60}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Meta Title (SEO)</label>
          <input
            type="text"
            name="metaTitle"
            value={formData.metaTitle || ''}
            onChange={handleChange}
            className="input"
          />
        </div>

        <div className="form-group">
          <label>Meta Description (SEO)</label>
          <textarea
            name="metaDescription"
            value={formData.metaDescription || ''}
            onChange={handleChange}
            className="input"
            rows="3"
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onCancel}
          >
            Cancel
          </button>
          {onPreview ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                const live = blogContentEditorRef.current?.getHtml?.() ?? '';
                const raw =
                  normalizeQuillEmpty(live).length > 0 ? live : (formData.content || '');
                const contentNorm = normalizeQuillHtml(raw);
                setFormData((prev) => ({ ...prev, content: contentNorm }));
                queueMicrotask(() =>
                  blogContentEditorRef.current?.applyHtml?.(contentNorm)
                );
                onPreview({
                  id: item?.id,
                  title: (formData.title && String(formData.title).trim()) || 'Untitled',
                  slug: formData.slug || '',
                  category: formData.category || '',
                  imageUrl: formData.imageUrl || '',
                  shortDescription: formData.shortDescription || '',
                  content: contentNorm || '',
                  author: formData.author || '',
                  publishedAt: formData.publishedAt,
                  isPublished: formData.isPublished
                });
              }}
            >
              <Eye size={18} className="content-icon-right" />
              Preview
            </button>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : (isEditing ? 'Update Blog' : 'Create Blog')}
          </button>
        </div>
      </form>
    );
  }

  if (type === 'testimonials') {
    return (
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name || ''}
            onChange={handleChange}
            className="input"
            placeholder="Reviewer full name"
            required
          />
        </div>

        <div className="form-group">
          <label>Stars (Rating) *</label>
          <select
            name="rating"
            value={formData.rating ?? 5}
            onChange={handleChange}
            className="input"
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>

        <ImageUpload
          label="Profile Image"
          value={formData.imageUrl || ''}
          onChange={(value) => setFormData(prev => ({ ...prev, imageUrl: value }))}
          uploadImage={async (file) => {
            const res = await adminAPI.uploadImage(file);
            return res.data?.data?.url || res.data?.url;
          }}
        />
        <p className="form-hint">Avatar/photo shown next to the testimonial</p>

        <div className="form-group">
          <label>Review *</label>
          <textarea
            name="quote"
            value={formData.quote || ''}
            onChange={handleChange}
            className="input"
            rows="4"
            placeholder="Customer review text"
            required
          />
        </div>

        <div className="form-group">
          <label>Gallery Images</label>
          <MultiImageUpload
            label="Review images"
            value={formData.galleryImages || []}
            onChange={(images) => setFormData(prev => ({ ...prev, galleryImages: images }))}
            maxImages={8}
          />
          <p className="form-hint">Optional images to show with this testimonial (e.g. trip photos)</p>
        </div>

        <div className="form-group">
          <label>Role / Title</label>
          <input
            type="text"
            name="role"
            value={formData.role || ''}
            onChange={handleChange}
            className="input"
            placeholder="e.g. Trekker, Participant"
          />
        </div>

        <div className="form-group">
          <label>Event name</label>
          <input
            type="text"
            name="eventName"
            value={formData.eventName || ''}
            onChange={handleChange}
            className="input"
            placeholder="e.g. Roopkund Trek 2024"
          />
        </div>

        <div className="form-group">
          <label>Display order</label>
          <input
            type="number"
            name="order"
            value={formData.order ?? 0}
            onChange={handleChange}
            className="input"
            min={0}
          />
          <p className="form-hint">Lower numbers appear first</p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive !== false}
              onChange={handleChange}
            />
            Active (show on website)
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : (isEditing ? 'Update Testimonial' : 'Add Testimonial')}
          </button>
        </div>
      </form>
    );
  }

  if (type === 'partners') {
    return (
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label>Partner Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name || ''}
            onChange={handleChange}
            className="input"
            placeholder="e.g. Incredible India"
            required
          />
        </div>

        <ImageUpload
          label="Partner Logo *"
          value={formData.logoUrl || ''}
          onChange={(value) => setFormData(prev => ({ ...prev, logoUrl: value }))}
          required
          uploadImage={async (file) => {
            const res = await adminAPI.uploadImage(file);
            return res.data?.data?.url || res.data?.url;
          }}
        />

        <div className="form-group">
          <label>Website URL</label>
          <input
            type="url"
            name="websiteUrl"
            value={formData.websiteUrl || ''}
            onChange={handleChange}
            className="input"
            placeholder="https://example.com"
          />
        </div>

        <div className="form-group">
          <label>Display Order</label>
          <input
            type="number"
            name="order"
            value={formData.order ?? 0}
            onChange={handleChange}
            className="input"
            min={0}
          />
          <p className="form-hint">Lower numbers appear first</p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive !== false}
              onChange={handleChange}
            />
            Active (show on website)
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : (isEditing ? 'Update Partner' : 'Add Partner')}
          </button>
        </div>
      </form>
    );
  }

  if (type === 'login-page-images') {
    return (
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label>Position (1–9) *</label>
          <input
            type="number"
            name="position"
            value={formData.position ?? 1}
            onChange={handleChange}
            className="input"
            min={1}
            max={9}
            required
          />
          <p className="form-hint">Controls where the tile appears on the login grid.</p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isSpecial"
              checked={formData.isSpecial === true}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  isSpecial: checked,
                  // If switching to special card, clear imageUrl (special cards render text-only).
                  ...(checked ? { imageUrl: '' } : {})
                }));
              }}
            />
            Special Card (text-only)
          </label>
          <p className="form-hint">Special cards show a title/subtitle instead of an image.</p>
        </div>

        {formData.isSpecial ? (
          <>
            <div className="form-group">
              <label>Special Title *</label>
              <input
                type="text"
                name="specialTitle"
                value={formData.specialTitle || ''}
                onChange={handleChange}
                className="input"
                placeholder="e.g. HIKER SUMMIT"
                required
              />
            </div>
            <div className="form-group">
              <label>Special Subtitle *</label>
              <input
                type="text"
                name="specialSubtitle"
                value={formData.specialSubtitle || ''}
                onChange={handleChange}
                className="input"
                placeholder="e.g. EXPLORE NOW"
                required
              />
            </div>
          </>
        ) : (
          <ImageUpload
            label="Login Image *"
            value={formData.imageUrl || ''}
            onChange={(value) => setFormData((prev) => ({ ...prev, imageUrl: value }))}
            required
            uploadImage={async (file) => {
              const res = await adminAPI.uploadImage(file);
              return res.data?.data?.url || res.data?.url;
            }}
          />
        )}

        <div className="form-group">
          <label>Display Order</label>
          <input
            type="number"
            name="order"
            value={formData.order ?? 0}
            onChange={handleChange}
            className="input"
            min={0}
          />
          <p className="form-hint">Lower numbers appear first within the same position.</p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive !== false}
              onChange={handleChange}
            />
            Active (show on website)
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            <Save size={18} className="content-icon-right" />
            {isLoading ? 'Saving...' : (isEditing ? 'Update Login Image' : 'Add Login Image')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="content-form">
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="input"
          required
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          <Save size={18} className="content-icon-right" />
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

function getDefaultFormData(type) {
  switch (type) {
    case 'hero-sliders':
      return {
        title: '',
        subtitle: '',
        imageUrl: '',
        imageUrls: [],
        useMultipleImages: false,
        ctaText: 'Explore Camps',
        ctaLink: '',
        isActive: true,
        order: 0
      };
    case 'stats':
      return {
        stats: [
          { label: 'Participants', value: '' },
          { label: 'Volunteers', value: '' },
          { label: 'Events', value: '' },
          { label: 'Years', value: '' },
        ]
      };
    case 'login-page-images':
      return {
        imageUrl: '',
        position: 1,
        isSpecial: false,
        specialTitle: 'HIKER SUMMIT',
        specialSubtitle: 'EXPLORE NOW',
        isActive: true,
        order: 0
      };
    case 'blogs':
      return {
        title: '',
        slug: '',
        category: '',
        imageUrl: '',
        shortDescription: '',
        content: '',
        author: 'Admin',
        isPublished: true,
        publishedAt: '',
        metaTitle: '',
        metaDescription: ''
      };
    case 'testimonials':
      return {
        name: '',
        role: '',
        imageUrl: '',
        quote: '',
        rating: 5,
        eventName: '',
        galleryImages: [],
        isActive: true,
        order: 0
      };
    case 'partners':
      return {
        name: '',
        logoUrl: '',
        websiteUrl: '',
        isActive: true,
        order: 0
      };
    default:
      return { title: '', isActive: true };
  }
}

export default Content;
